import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { StatusBadge } from '@pazportal/ui';
import { useAuth } from '../contexts/AuthContext';
import { propertyCodeOf } from '../hooks/usePropertyDirectory';
import { missionControlCandidates, missionControlLinkError } from '../lib/missionControlLinks';
import { supabase } from '../lib/supabase';
import { MissionControlDataHandoff } from './MissionControlDataHandoff';

export function MissionControlSiteLinks({ locationId }: { locationId: string }) {
  const { user, role } = useAuth();
  const queryClient = useQueryClient();
  const [siteId, setSiteId] = useState('');
  const [label, setLabel] = useState('');
  const [reason, setReason] = useState('');
  const [reviewReason, setReviewReason] = useState('');
  const canManage = role === 'admin' || role === 'superadmin';
  const queryKey = ['mission-control-site-links', user?.id, locationId];
  const links = useQuery({
    queryKey,
    enabled: Boolean(user?.id && locationId),
    queryFn: async () => {
      const { data, error } = await supabase.from('mission_control_site_links')
        .select('id, location_id, source_instance, mission_control_site_id, label, status, version, last_reason, reviewed_at, staff_enabled, customer_enabled')
        .eq('location_id', locationId).order('created_at');
      if (error) throw error;
      return data;
    },
  });
  const candidates = useQuery({
    queryKey: ['mission-control-site-candidates', user?.id, locationId],
    enabled: Boolean(canManage && user?.id && locationId),
    queryFn: async () => {
      const location = await supabase.from('locations').select('hierarchy').eq('id', locationId).single();
      if (location.error) throw location.error;
      const code = propertyCodeOf(location.data.hierarchy);
      if (!code) return [];
      const directory = await supabase.from('property_directory')
        .select('name, mission_control_site_id, additional_networks').eq('code', code).maybeSingle();
      if (directory.error) throw directory.error;
      return missionControlCandidates(directory.data);
    },
  });
  const create = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('create_mission_control_site_link', {
        p_location_id: locationId, p_mission_control_site_id: siteId.trim(),
        p_label: label.trim(), p_reason: reason.trim(),
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      setSiteId(''); setLabel(''); setReason('');
      await queryClient.invalidateQueries({ queryKey });
    },
  });
  const review = useMutation({
    mutationFn: async (input: { id: string; version: number; status: string }) => {
      const { error } = await supabase.rpc('review_mission_control_site_link', {
        p_link_id: input.id, p_expected_version: input.version,
        p_status: input.status, p_reason: reviewReason.trim(),
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      setReviewReason('');
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: async () => { await queryClient.invalidateQueries({ queryKey }); },
  });
  const access = useMutation({
    mutationFn: async (input: { id: string; version: number; staff: boolean; customer: boolean }) => {
      const { error } = await supabase.rpc('set_mission_control_site_access', {
        p_link_id: input.id, p_expected_version: input.version,
        p_staff_enabled: input.staff, p_customer_enabled: input.customer,
        p_reason: reviewReason.trim(),
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      setReviewReason('');
      await queryClient.invalidateQueries({ queryKey });
      await queryClient.invalidateQueries({ queryKey: ['mission-control-networks'] });
    },
    onError: async () => { await queryClient.invalidateQueries({ queryKey }); },
  });
  const saving = create.isPending || review.isPending || access.isPending;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    review.reset();
    access.reset();
    create.mutate();
  };
  const error = create.error ?? review.error ?? access.error;
  const availableCandidates = (candidates.data ?? []).filter((candidate) =>
    !links.data?.some((link) => link.source_instance === 'primary'
      && link.mission_control_site_id === candidate.siteId && link.status !== 'disabled'));

  return (
    <section aria-labelledby="mission-control-links-title" className="rounded-lg border border-border bg-surface p-5 space-y-4">
      <div>
        <h2 id="mission-control-links-title" className="font-semibold text-text">Mission Control networks</h2>
        <p className="mt-1 text-sm text-text-muted">Review which networks belong to this location, enable them for the operations team, then choose when to publish the customer monitoring summary.</p>
      </div>
      {links.isLoading && <p role="status" className="text-sm text-text-muted">Loading site links…</p>}
      {links.isError && <div role="alert" className="text-sm text-error">Site links could not be loaded. <button type="button" onClick={() => void links.refetch()} className="underline">Try again</button></div>}
      {!links.isLoading && !links.isError && !links.data?.length && <p className="text-sm text-text-muted">No networks linked yet.</p>}
      {links.data && links.data.length > 0 && (
        <ul className="divide-y divide-divider">
          {links.data.map((link) => (
            <li key={link.id} className="py-3 space-y-2">
              <div className="flex flex-wrap justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text">{link.label}</p>
                  <p className="break-all text-xs text-text-muted">{link.source_instance} · {link.mission_control_site_id}</p>
                </div>
                <StatusBadge status={link.status === 'verified' ? 'active' : link.status === 'disabled' ? 'inactive' : 'pending'} label={link.status === 'verified' ? 'Identity reviewed' : link.status === 'disabled' ? 'Disabled' : 'Pending review'} />
              </div>
              <p className="text-xs text-text-muted">{link.last_reason}</p>
              <p className="text-xs text-text-muted">Operations: {link.staff_enabled ? 'Enabled' : 'Off'} · Customer monitoring summary: {link.customer_enabled ? 'Published' : 'Hidden'}</p>
              {canManage && (
                <div className="flex flex-wrap gap-3 text-xs">
                  {link.status !== 'verified' && <button type="button" disabled={saving || reviewReason.trim().length < 5} onClick={() => { create.reset(); access.reset(); review.mutate({ id: link.id, version: link.version, status: 'verified' }); }} className="text-primary disabled:opacity-40">Mark identity reviewed</button>}
                  {link.status !== 'disabled' && <button type="button" disabled={saving || reviewReason.trim().length < 5} onClick={() => { create.reset(); access.reset(); review.mutate({ id: link.id, version: link.version, status: 'disabled' }); }} className="text-error disabled:opacity-40">Disable link</button>}
                  {link.status === 'verified' && <button type="button" disabled={saving || reviewReason.trim().length < 5} onClick={() => {
                    create.reset(); review.reset();
                    access.mutate({ id: link.id, version: link.version, staff: !link.staff_enabled, customer: false });
                  }} className="text-primary disabled:opacity-40">{link.staff_enabled ? 'Turn off operations access' : 'Enable operations access'}</button>}
                  {link.status === 'verified' && link.staff_enabled && <button type="button" disabled={saving || reviewReason.trim().length < 5} onClick={() => {
                    create.reset(); review.reset();
                    access.mutate({ id: link.id, version: link.version, staff: true, customer: !link.customer_enabled });
                  }} className="text-primary disabled:opacity-40">{link.customer_enabled ? 'Hide customer monitoring summary' : 'Publish customer monitoring summary'}</button>}
                </div>
              )}
              <MissionControlDataHandoff linkId={link.id} linkVersion={link.version} locationId={locationId}
                staffEnabled={link.staff_enabled} linkStatus={link.status} />
            </li>
          ))}
        </ul>
      )}
      {canManage && !links.isError && !links.isLoading && (
        <div className="space-y-4 border-t border-border pt-4">
          {links.data && links.data.length > 0 && <label className="block text-sm text-text">Reason for reviewing or changing network access
            <input value={reviewReason} onChange={(event) => setReviewReason(event.target.value)} maxLength={1000} disabled={saving} className="mt-1 w-full rounded border border-border bg-surface-2 p-2" placeholder="What did you check?" />
          </label>}
          {candidates.isError && <p role="alert" className="text-sm text-warning">Directory suggestions could not be loaded. You can enter a site ID below.</p>}
          {availableCandidates.length > 0 && <div>
            <p className="text-xs text-text-muted mb-2">Directory suggestions — check each network before linking it.</p>
            <div className="flex flex-wrap gap-2">{availableCandidates.map((candidate) => <button key={candidate.siteId} type="button" disabled={saving} onClick={() => { setSiteId(candidate.siteId); setLabel(candidate.label); }} className="rounded border border-border px-3 py-2 text-xs text-primary disabled:opacity-40">{candidate.label}</button>)}</div>
          </div>}
          <form onSubmit={submit} className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-sm text-text">Mission Control site ID
                <input required value={siteId} onChange={(event) => setSiteId(event.target.value)} pattern="[A-Za-z0-9][A-Za-z0-9._:\-]{0,191}" maxLength={192} disabled={saving} className="mt-1 w-full rounded border border-border bg-surface-2 p-2" />
              </label>
              <label className="block text-sm text-text">Network label
                <input required value={label} onChange={(event) => setLabel(event.target.value)} maxLength={160} disabled={saving} className="mt-1 w-full rounded border border-border bg-surface-2 p-2" />
              </label>
            </div>
            <label className="block text-sm text-text">Reason for linking
              <input required value={reason} onChange={(event) => setReason(event.target.value)} minLength={5} maxLength={1000} disabled={saving} className="mt-1 w-full rounded border border-border bg-surface-2 p-2" />
            </label>
            <button type="submit" disabled={saving || !siteId.trim() || !label.trim() || reason.trim().length < 5} className="rounded bg-primary px-4 py-2 text-sm text-white disabled:opacity-40">{create.isPending ? 'Adding…' : 'Add pending link'}</button>
          </form>
        </div>
      )}
      {error && <p role="alert" className="text-sm text-error">{missionControlLinkError(error)}</p>}
    </section>
  );
}
